import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, Printer } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Course, Enrollment, User } from '@/types';
import { supabase } from "@/integrations/supabase/client";

// Real API calls to Supabase
const UserAPI = {
  me: async () => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('No authenticated user');
    
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (profileError) throw profileError;
    const profileData = profile as any;
    return {
      id: user.id,
      email: user.email,
      full_name: `${profileData.first_name} ${profileData.last_name}`,
      job_title: profileData.position_title,
      site: profileData.school_name,
      role: profileData.is_admin ? 'admin' : 'user',
      district_id: profileData.district_id,
      pd_hours_completed: profileData.pd_hours_completed || 0,
      required_pd_hours: profileData.required_pd_hours || 40,
      onboarding_state: profileData.onboarding_state || {},
      ...profileData
    };
  },
};

const EnrollmentAPI = {
  filter: async (filters: any) => {
    const { data, error } = await supabase
      .from('enrollments')
      .select('*')
      .match(filters);
    
    if (error) throw error;
    return data.map(enrollment => ({
      ...enrollment,
      user_id: enrollment.participant_id,
      course_id: enrollment.course_id,
      status: enrollment.status,
      completion_date: enrollment.completion_date,
      enrollment_date: enrollment.enrollment_date
    }));
  },
};

const CourseAPI = {
  get: async (id: string) => {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        course_categories(name)
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return {
      ...data,
      category: data.course_categories?.name || 'other',
      delivery_method: data.is_online ? 'synchronous_online' : 'in_person',
      pd_hours: data.duration_hours || 0,
      target_audience: ['all'],
      status: 'active'
    };
  },
};

export default function Transcript() {
  const [user, setUser] = useState<User | null>(null);
  const [completedCourses, setCompletedCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTranscriptData() {
      try {
        const currentUser = await UserAPI.me();
        setUser(currentUser);

        const enrollments = await EnrollmentAPI.filter({
          participant_id: currentUser.id,
          status: 'completed'
        });

        const coursesWithDetails = await Promise.all(
          enrollments.map(async (enrollment) => {
            try {
              const course = await CourseAPI.get(enrollment.course_id);
              return { ...course, enrollment };
            } catch (error) {
              console.error(`Could not load course ${enrollment.course_id}`, error);
              return null;
            }
          })
        );
        
        const validCourses = coursesWithDetails.filter((c): c is any => c !== null);
        const sortedCourses = validCourses.sort((a, b) => {
          const dateA = new Date(a.enrollment?.completion_date || 0);
          const dateB = new Date(b.enrollment?.completion_date || 0);
          return dateB.getTime() - dateA.getTime();
        });
        
        setCompletedCourses(sortedCourses);

      } catch (error) {
        console.error("Failed to load transcript data:", error);
        toast.error('Could not load your transcript.');
      } finally {
        setLoading(false);
      }
    }
    loadTranscriptData();
  }, []);

  const totalPdHours = completedCourses.reduce((sum, course) => sum + (course.pd_hours || 0), 0);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Course Title,Category,Completion Date,PD Hours\n";

    completedCourses.forEach(course => {
      const row = [
        `"${course.title}"`,
        course.category || '',
        course.enrollment?.completion_date ? format(new Date(course.enrollment.completion_date), 'yyyy-MM-dd') : '',
        course.pd_hours
      ].join(',');
      csvContent += row + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `transcript_${user?.full_name?.replace(' ', '_') || 'user'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" id="transcript-content">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #transcript-content, #transcript-content * {
            visibility: visible;
          }
          #transcript-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none;
          }
        }
      `}</style>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">My Transcript</h1>
          <p className="text-muted-foreground mt-1">Official record of your completed professional development.</p>
        </div>
        <div className="flex gap-2 no-print">
          <Button variant="outline" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
          <Button onClick={handlePrint} className="bg-gradient-primary hover:bg-primary/90">
            <Printer className="w-4 h-4 mr-2" /> Print Transcript
          </Button>
        </div>
      </div>

      <Card className="mb-8 bg-gradient-card border-primary/10 shadow-lg">
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Name</h3>
            <p className="text-lg font-semibold text-foreground">{user?.full_name}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Email</h3>
            <p className="text-lg font-semibold text-foreground">{user?.email}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Total PD Hours</h3>
            <p className="text-lg font-semibold text-primary">{totalPdHours}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-lg bg-card">
        <CardHeader>
          <CardTitle className="text-foreground">Completed Courses</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Course Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Delivery Method</TableHead>
                <TableHead>Completion Date</TableHead>
                <TableHead className="text-right">PD Hours</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {completedCourses.length > 0 ? (
                completedCourses.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell className="font-medium">{course.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{course.category}</Badge>
                    </TableCell>
                    <TableCell className="capitalize">{course.delivery_method?.replace('_', ' ')}</TableCell>
                    <TableCell>
                      {course.enrollment?.completion_date ? 
                        format(new Date(course.enrollment.completion_date), 'MMM d, yyyy') : 
                        'N/A'
                      }
                    </TableCell>
                    <TableCell className="text-right font-medium text-primary">{course.pd_hours}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    No completed courses yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}